import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getAllUsers,
  toggleUserActiveStatus,
  setUserPlan,
  resetUserMessages,
  UserResponse,
  getAdminSessions,
  getAdminPlanStats,
  PlanStatsResponse,
  SessionLogItem,
  getPaymentLogs,
  logout,
  getAdminTickets,
  getTicketDetails,
  replyToTicket,
  closeTicket,
  SupportTicket,
  MessageHistoryItem,
  getWhatsAppStatus,
  getSystemMetrics,
  getSystemLogs,
  reconnectWhatsApp,
  WhatsAppStatus,
  SystemMetrics,
} from '../services/api';
import TokenUsage from '../components/TokenUsage';
import { useNavigate } from 'react-router-dom';

interface PaymentLog {
  id: number;
  user_id: number;
  user_email: string;
  event_type: string;
  plan: string;
  timestamp: string;
  details: string;
}

const AdminDashboardPage = () => {
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<UserResponse[]>([]);
  const [sessions, setSessions] = useState<SessionLogItem[]>([]);
  const [isMaster, setIsMaster] = useState(false);
  const [planStats, setPlanStats] = useState<PlanStatsResponse | null>(null);
  const [paymentLogs, setPaymentLogs] = useState<PaymentLog[]>([]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketHistory, setTicketHistory] = useState<MessageHistoryItem[]>([]);
  const [ticketReply, setTicketReply] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'admins' | 'stats' | 'alerts' | 'comms' | 'devops' | 'tickets'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermComms, setSearchTermComms] = useState('');
  const [currentUser, setCurrentUser] = useState<UserResponse | null>(null);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifTarget, setNotifTarget] = useState<'ALL' | 'INDIVIDUAL' | 'MULTIPLE' | 'PLAN'>('ALL');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('free');
  const [sendingNotif, setSendingNotif] = useState(false);
  const [commsStatus, setCommsStatus] = useState<{ type: 'success' | 'error', msg: string } | null>(null);
  const [devopsChat, setDevopsChat] = useState<{ role: string, content: string }[]>([]);
  const [devopsInput, setDevopsInput] = useState('');
  const [devopsHealth, setDevopsHealth] = useState<any>(null);
  const [devopsLoading, setDevopsLoading] = useState(false);
  const [devopsError, setDevopsError] = useState<string | null>(null);
  const [devopsSuccess, setDevopsSuccess] = useState<string | null>(null);
  const [showEscalatedOnly, setShowEscalatedOnly] = useState(false);
  // --- Monitor state ---
  const [waStatus, setWaStatus] = useState<WhatsAppStatus | null>(null);
  const [waLoading, setWaLoading] = useState(false);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [sysLogs, setSysLogs] = useState<string[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectMsg, setReconnectMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const monitorIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const me = await import('../services/api').then(mod => mod.getCurrentUser());
      if (me) setCurrentUser(me);

      const fetchedUsers = await getAllUsers();
      if (fetchedUsers) {
        setUsers(fetchedUsers);
      }

      try {
        const { getAdminRequests } = await import('../services/api');
        const fetchedRequests = await getAdminRequests();
        if (fetchedRequests) {
          setRequests(fetchedRequests);
          setIsMaster(true);

          // Also fetch sessions for Master
          const fetchedSessions = await getAdminSessions();
          if (fetchedSessions) setSessions(fetchedSessions);

          const fetchedStats = await getAdminPlanStats();
          if (fetchedStats) setPlanStats(fetchedStats);

          const logs = await getPaymentLogs();
          if (logs) setPaymentLogs(logs);
        }
      } catch (e) {
        setIsMaster(false);
      }

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggleActive = async (userId: number) => {
    try {
      await toggleUserActiveStatus(userId);
      fetchUsers();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleResetMessages = async (userId: number, userEmail: string) => {
    if (!window.confirm(`¿Resetear el contador de mensajes de ${userEmail}? Se reiniciará su ciclo.`)) return;
    try {
      await resetUserMessages(userId);
      alert('✅ Contador reseteado correctamente.');
      fetchUsers();
    } catch (err: any) {
      alert('Error al resetear: ' + err.message);
    }
  };

  const handleSetPlan = async (userId: number, newPlan: string) => {
    if (newPlan.toLowerCase() === 'vip' && !isMaster) {
      alert("Solo el Administrador Maestro puede otorgar el plan VIP.");
      return;
    }
    try {
      await setUserPlan(userId, newPlan);
      fetchUsers();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleApprove = async (userId: number) => {
    try {
      const { approveAdmin } = await import('../services/api');
      await approveAdmin(userId);
      fetchUsers();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleReject = async (userId: number) => {
    try {
      const { rejectAdmin } = await import('../services/api');
      await rejectAdmin(userId);
      fetchUsers();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const triggerDevOpsChat = async (message: string) => {
    if (!message.trim()) return;
    setDevopsChat(prev => [...prev, { role: 'user', content: message }]);
    setDevopsLoading(true);
    try {
      const { sendDevOpsMessage } = await import('../services/api');
      const res = await sendDevOpsMessage(message);
      if (res) {
        setDevopsChat(prev => [...prev, { role: 'assistant', content: res.response }]);
      }
    } catch (e: any) {
      setDevopsError(e.message);
      setTimeout(() => setDevopsError(null), 5000);
    } finally {
      setDevopsLoading(false);
    }
  };

  const handleClearDevOpsChat = async () => {
    if (!window.confirm("¿Seguro que quieres borrar todo el historial del agente DevOps?")) return;
    try {
      const { clearDevOpsHistory } = await import('../services/api');
      await clearDevOpsHistory();
      setDevopsChat([]);
      setDevopsSuccess("Historial borrado correctamente.");
      setTimeout(() => setDevopsSuccess(null), 3000);
    } catch (e: any) {
      setDevopsError(e.message);
    }
  };

  const handleDevOpsChat = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = devopsInput;
    setDevopsInput('');
    await triggerDevOpsChat(msg);
  };

  const fetchDevOpsInitialData = async () => {
    try {
      const { getDevOpsHistory } = await import('../services/api');
      const history = await getDevOpsHistory();
      if (history) setDevopsChat(history);
    } catch (e) { }
  };

  const fetchTickets = async () => {
    try {
      const res = await getAdminTickets();
      if (res) setTickets(res);
    } catch (e) { }
  };

  // ── Monitor polling ──────────────────────────────────────────────────────
  const fetchMonitorData = useCallback(async () => {
    try {
      const [wa, met, logs] = await Promise.all([
        getWhatsAppStatus(),
        getSystemMetrics(),
        getSystemLogs(),
      ]);
      if (wa) setWaStatus(wa);
      if (met) setMetrics(met);
      if (logs) setSysLogs(logs.logs);
      setLastRefresh(new Date());
    } catch (e) {
      console.warn('Monitor fetch failed', e);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'devops') {
      fetchDevOpsInitialData();
      fetchMonitorData();
      monitorIntervalRef.current = setInterval(fetchMonitorData, 30000);
    } else {
      if (monitorIntervalRef.current) clearInterval(monitorIntervalRef.current);
    }
    if (activeTab === 'tickets') fetchTickets();
    return () => {
      if (monitorIntervalRef.current) clearInterval(monitorIntervalRef.current);
    };
  }, [activeTab, fetchMonitorData]);

  const handleReconnectWhatsApp = async () => {
    setReconnecting(true);
    setReconnectMsg(null);
    try {
      const res = await reconnectWhatsApp();
      if (res) setReconnectMsg({ ok: res.ok, text: res.message });
      // Refetch status after 12s to let evolution restart
      setTimeout(fetchMonitorData, 12000);
    } catch (e: any) {
      setReconnectMsg({ ok: false, text: e.message });
    } finally {
      setReconnecting(false);
      setTimeout(() => setReconnectMsg(null), 8000);
    }
  };

  const handleSelectTicket = async (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setTicketLoading(true);
    // Optimistic reading
    setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, is_read: true } : t));
    try {
      const res = await getTicketDetails(ticket.id);
      if (res) {
        setTicketHistory(res.history);
      }
    } catch (e: any) {
      alert(e.message);
    } finally {
      setTicketLoading(false);
    }
  };

  const handleReplyTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket || !ticketReply.trim()) return;
    try {
      await replyToTicket(selectedTicket.id, ticketReply);
      const adminName = currentUser?.full_name.split(' ')[0] || 'Admin';
      setTicketHistory(prev => [...prev, { role: 'admin', content: `[${adminName}]: ${ticketReply}` }]);
      setTicketReply('');
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleCloseTicket = async (ticketId: number) => {
    if (!window.confirm("¿Seguro que quieres cerrar este ticket?")) return;
    try {
      await closeTicket(ticketId);
      fetchTickets();
      setSelectedTicket(null);
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer.')) {
      try {
        const { deleteUser } = await import('../services/api');
        await deleteUser(userId);
        fetchUsers();
      } catch (e: any) {
        alert(e.message);
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const addToSandbox = (userId: number) => {
    if (!selectedUserIds.includes(userId)) {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const removeFromSandbox = (userId: number) => {
    setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
  };

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notifMessage.trim()) return;

    try {
      setSendingNotif(true);
      const { sendAdminNotification } = await import('../services/api');
      await sendAdminNotification(
        notifMessage,
        notifTarget,
        notifTarget === 'INDIVIDUAL' ? parseInt(selectedUserId) : undefined,
        notifTarget === 'MULTIPLE' ? selectedUserIds : undefined,
        notifTarget === 'PLAN' ? selectedPlan : undefined
      );
      setCommsStatus({ type: 'success', msg: '¡Mensaje enviado con éxito a los destinos seleccionados!' });
      setNotifMessage('');
      setSelectedUserIds([]); // Clear sandbox
      setTimeout(() => setCommsStatus(null), 5000);
    } catch (err: any) {
      setCommsStatus({ type: 'error', msg: 'Error al enviar: ' + err.message });
    } finally {
      setSendingNotif(false);
    }
  };

  const [expandedAdmin, setExpandedAdmin] = useState<string | null>(null);

  const toggleAccordion = (adminName: string) => {
    setExpandedAdmin(expandedAdmin === adminName ? null : adminName);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  const filteredUsers = users.filter(u => {
    const search = searchTerm.toLowerCase();
    return u.full_name?.toLowerCase().includes(search) || u.email?.toLowerCase().includes(search);
  });

  const filteredUsersComms = users.filter(u => {
    const search = searchTermComms.toLowerCase();
    return !u.is_admin && (u.full_name?.toLowerCase().includes(search) || u.email?.toLowerCase().includes(search));
  });

  const regularUsers = filteredUsers.filter(u => !u.is_admin);
  const admins = filteredUsers.filter(u => u.is_admin);

  return (
    <div className="light-theme-page">
      <div className="container py-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-0">Panel de Control {isMaster ? 'Master' : 'Admin'}</h2>
            {currentUser && <p className="text-muted mb-0">Bienvenido, <strong>{currentUser.full_name}</strong></p>}
          </div>
          <div>
            <button className="btn btn-outline-primary me-2" onClick={fetchUsers}>
              <i className="bi bi-arrow-clockwise"></i> Refrescar
            </button>
            <button className="btn btn-outline-danger" onClick={handleLogout}>
              Cerrar Sesión
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              Control de Usuarios
            </button>
          </li>
          {isMaster && (
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'admins' ? 'active' : ''}`}
                onClick={() => setActiveTab('admins')}
              >
                Gestión de Administradores
              </button>
            </li>
          )}
          {isMaster && (
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'stats' ? 'active' : ''}`}
                onClick={() => setActiveTab('stats')}
              >
                Estadísticas Financieras
              </button>
            </li>
          )}
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'alerts' ? 'active' : ''}`}
              onClick={() => setActiveTab('alerts')}
            >
              Alertas de Pago <span className="badge bg-danger ms-1">{paymentLogs.filter(l => l.event_type !== 'ACTIVATION' && l.event_type !== 'RENEWAL').length}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'comms' ? 'active' : ''}`}
              onClick={() => setActiveTab('comms')}
            >
              Comunicaciones
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'devops' ? 'active' : ''}`}
              onClick={() => setActiveTab('devops')}
            >
              DevOps
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'tickets' ? 'active' : ''}`}
              onClick={() => setActiveTab('tickets')}
            >
              Reclamos <span className="badge bg-danger ms-1">{tickets.filter(t => t.status === 'open').length}</span>
            </button>
          </li>
        </ul>

        {activeTab === 'users' && (
          <>
            <TokenUsage />
            <div className="card shadow-sm mt-4">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Lista de Usuarios Registrados</h5>
                <div className="input-group input-group-sm w-25">
                  <span className="input-group-text"><i className="bi bi-search"></i></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por nombre o email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Plan</th>
                      <th>Fecha Suscr.</th>
                      <th>Mensajes</th>
                      <th>Consumo</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regularUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.full_name}</td>
                        <td>{user.email}</td>
                        <td>
                          <select
                            className="form-select form-select-sm w-auto"
                            value={user.plan.toLowerCase()}
                            onChange={(e) => handleSetPlan(user.id, e.target.value)}
                          >
                            <option value="free">FREE</option>
                            <option value="medio">MEDIO</option>
                            <option value="full">FULL</option>
                            <option value="trial">TRIAL</option>
                            <option value="vip">VIP</option>
                          </select>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.85rem' }}>
                            <div><span className="text-muted">Alta:</span> {user.subscription?.subscription_date ? new Date(user.subscription.subscription_date).toLocaleDateString() : 'N/A'}</div>
                            {user.subscription?.status === 'cancelled' ? (
                              <div>
                                <span className="text-muted">Ciclo:</span> <span className="badge bg-secondary">Baja ({user.subscription.cycle_end_date ? new Date(user.subscription.cycle_end_date).toLocaleDateString() : 'Finalizado'})</span>
                              </div>
                            ) : (
                              <div><span className="text-muted">Ciclo:</span> {user.subscription?.cycle_start_date ? new Date(user.subscription.cycle_start_date).toLocaleDateString() : 'N/A'}</div>
                            )}
                            {user.subscription?.is_cancelled && user.subscription?.status !== 'cancelled' && (
                              <div className="text-warning fw-bold mt-1" style={{ fontSize: '0.75rem' }}>
                                ⚠️ Sol. Baja (hasta {user.subscription.cycle_end_date ? new Date(user.subscription.cycle_end_date).toLocaleDateString() : 'fin de ciclo'})
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{user.subscription?.message_count ?? 0}</td>
                        <td>{user.token_usage}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="form-check form-switch me-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={user.is_active}
                                onChange={() => handleToggleActive(user.id)}
                              />
                            </div>
                            {user.subscription?.status === 'cancelled' ? (
                              <span className="badge bg-danger">Baja</span>
                            ) : user.subscription?.is_cancelled ? (
                              <span className="badge bg-warning text-dark" title="Solicitó baja pero sigue activo hasta fin de ciclo">
                                Sol. Baja
                              </span>
                            ) : (
                              <span className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                {user.is_active ? 'Activo' : 'Inactivo'}
                              </span>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              title="Resetear contador de mensajes"
                              onClick={() => handleResetMessages(user.id, user.email)}
                            >
                              🔄 Reset
                            </button>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteUser(user.id)}>
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === 'admins' && isMaster && (
          <div className="animate__animated animate__fadeIn">
            {/* Pending Requests */}
            {requests.length > 0 && (
              <div className="card mb-4 border-warning shadow-sm">
                <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                  <strong>Solicitudes de Administrador Pendientes</strong>
                  <span className="badge bg-dark">{requests.length}</span>
                </div>
                <div className="table-responsive">
                  <table className="table table-striped align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Email</th>
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map(req => (
                        <tr key={req.id}>
                          <td>{req.full_name}</td>
                          <td>{req.email}</td>
                          <td>
                            <button className="btn btn-sm btn-success me-2" onClick={() => handleApprove(req.id)}>Autorizar</button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleReject(req.id)}>Rechazar</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Existing Admins */}
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">Administradores del Sistema</h5>
              </div>
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Estado Labores</th>
                      <th>Tipo</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map(admin => (
                      <tr key={admin.id}>
                        <td>{admin.full_name}</td>
                        <td>{admin.email}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={admin.is_active}
                              disabled={admin.is_master}
                              onChange={() => handleToggleActive(admin.id)}
                            />
                            <span className={`badge ${admin.is_active ? 'bg-success' : 'bg-secondary'} ms-2`}>
                              {admin.is_active ? 'Autorizado' : 'Inactivo'}
                            </span>
                          </div>
                        </td>
                        <td>{admin.is_master ? <span className="badge bg-dark">Master</span> : 'Admin'}</td>
                        <td>
                          {!admin.is_master && (
                            <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteUser(admin.id)}>
                              Eliminar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Session History Grouped */}
            <div className="card shadow-sm">
              <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Historial de Conexiones por Administrador</h5>
                <span className="badge bg-secondary">{sessions.length} registros totales</span>
              </div>
              <div className="card-body p-0">
                <div className="accordion accordion-flush" id="adminSessionsAccordion">
                  {Object.entries(
                    sessions.reduce((acc, log) => {
                      const name = log.full_name || `ID: ${log.user_id}`;
                      if (!acc[name]) acc[name] = [];
                      acc[name].push(log);
                      return acc;
                    }, {} as Record<string, SessionLogItem[]>)
                  ).map(([adminName, adminLogs]) => {
                    const isOpen = expandedAdmin === adminName;
                    return (
                      <div className="accordion-item" key={adminName}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${isOpen ? '' : 'collapsed'}`}
                            type="button"
                            onClick={() => toggleAccordion(adminName)}
                          >
                            <div className="d-flex justify-content-between w-100 me-3">
                              <span><strong>{adminName}</strong></span>
                              <span className="badge bg-light text-dark">{adminLogs.length} sesiones</span>
                            </div>
                          </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${isOpen ? 'show' : ''}`}>
                          <div className="accordion-body p-0">
                            <div className="table-responsive">
                              <table className="table table-sm table-hover mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Acción</th>
                                    <th>Fecha</th>
                                    <th>Hora</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {adminLogs.map(log => (
                                    <tr key={log.id}>
                                      <td>
                                        <span className={`badge ${log.action === 'login' ? 'bg-success' : 'bg-warning'} text-dark`}>
                                          {log.action === 'login' ? 'INICIO SESIÓN' : 'CIERRE SESIÓN'}
                                        </span>
                                      </td>
                                      <td>{new Date(log.timestamp).toLocaleDateString()}</td>
                                      <td>{new Date(log.timestamp).toLocaleTimeString()}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {sessions.length === 0 && (
                    <div className="p-4 text-center text-muted">No hay registros de sesiones disponibles.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'stats' && isMaster && planStats && (
          <div className="animate__animated animate__fadeIn">
            <div className="row">
              <div className="col-md-8">
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">Distribución de Planes y Recaudación</h5>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Plan</th>
                          <th>Usuarios Suscritos</th>
                          <th>Total Importe (estimado)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {planStats.plans.map(p => (
                          <tr key={p.plan}>
                            <td className="text-uppercase fw-bold">{p.plan}</td>
                            <td>{p.count} usuarios</td>
                            <td>${p.total_importe} USD</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr className="fw-bold">
                          <td colSpan={2} className="text-end">TOTAL RECAUDADO:</td>
                          <td>${planStats.total_general} USD</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm mb-4 text-center p-4 bg-primary text-white">
                  <h3>Recaudación Total</h3>
                  <div className="display-4 fw-bold">${planStats.total_general}</div>
                  <p className="mt-2 mb-0">USD por ciclo mensual</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="animate__animated animate__fadeIn">
            <div className="card shadow-sm">
              <div className="card-header bg-danger text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Alertas y Eventos de Pago</h5>
                <small>Eventos críticos que requieren atención</small>
              </div>
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Evento</th>
                      <th>Usuario</th>
                      <th>Plan</th>
                      <th>Fecha</th>
                      <th>Detalles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentLogs.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-4 text-muted">No hay alertas de pago recientes.</td></tr>
                    ) : (
                      paymentLogs.map(log => (
                        <tr key={log.id} className={log.event_type === 'FAILURE' ? 'table-danger' : log.event_type === 'CANCELLATION' ? 'table-warning' : ''}>
                          <td>
                            <span className={`badge ${log.event_type === 'FAILURE' ? 'bg-danger' :
                              log.event_type === 'CANCELLATION' ? 'bg-warning text-dark' :
                                log.event_type === 'ACTIVATION' ? 'bg-success' : 'bg-info'
                              }`}>
                              {log.event_type}
                            </span>
                          </td>
                          <td>{log.user_email}</td>
                          <td>{log.plan}</td>
                          <td>{new Date(log.timestamp).toLocaleString()}</td>
                          <td><small>{log.details}</small></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'comms' && (
          <div className="animate__animated animate__fadeIn">
            <div className="row justify-content-center">
              <div className="col-md-6">
                <div className="card shadow-sm">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">Enviar Notificación / Aviso</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleSendNotification}>
                      <div className="mb-3">
                        <label className="form-label">Destinatarios</label>
                        <select
                          className="form-select"
                          value={notifTarget}
                          onChange={(e) => {
                            setNotifTarget(e.target.value as any);
                            setSearchTermComms('');
                          }}
                        >
                          <option value="ALL">📢 Todos los usuarios</option>
                          <option value="MULTIPLE">👥 Varios usuarios (Sandbox)</option>
                          <option value="PLAN">📋 Selección por plan</option>
                          <option value="INDIVIDUAL">👤 Usuario específico</option>
                        </select>
                      </div>

                      {/* --- INDIVIDUAL TARGET --- */}
                      {notifTarget === 'INDIVIDUAL' && (
                        <div className="mb-4">
                          <label className="form-label fw-bold">Buscar Usuario</label>
                          <div className="input-group mb-2">
                            <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-primary"></i></span>
                            <input
                              type="text"
                              className="form-control border-start-0 ps-0"
                              placeholder="Filtra por nombre o email..."
                              value={searchTermComms}
                              onChange={(e) => setSearchTermComms(e.target.value)}
                            />
                          </div>
                          <select
                            className="form-select"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            required
                          >
                            <option value="">-- Elige un usuario --</option>
                            {filteredUsersComms.sort((a, b) => a.full_name?.localeCompare(b.full_name || '') || 0).map(u => (
                              <option key={u.id} value={u.id}>
                                {u.full_name} ({u.email})
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* --- MULTIPLE TARGET (SANDBOX) --- */}
                      {notifTarget === 'MULTIPLE' && (
                        <div className="mb-4">
                          <label className="form-label fw-bold">Añadir usuarios al Sandbox</label>
                          <div className="input-group mb-2">
                            <span className="input-group-text bg-white border-end-0"><i className="bi bi-person-plus text-success"></i></span>
                            <input
                              type="text"
                              className="form-control border-start-0 ps-0"
                              placeholder="Busca por nombre para añadir..."
                              value={searchTermComms}
                              onChange={(e) => setSearchTermComms(e.target.value)}
                            />
                          </div>

                          {/* Dropdown Selector */}
                          <div className="mb-3">
                            <select
                              className="form-select"
                              onChange={(e) => {
                                if (e.target.value) {
                                  addToSandbox(parseInt(e.target.value));
                                  e.target.value = ''; // Reset selector
                                }
                              }}
                              value=""
                            >
                              <option value="">-- Haz clic para elegir y añadir al Sandbox --</option>
                              {filteredUsersComms
                                .filter(u => !selectedUserIds.includes(u.id))
                                .sort((a, b) => a.full_name?.localeCompare(b.full_name || '') || 0)
                                .map(u => (
                                  <option key={u.id} value={u.id}>
                                    {u.full_name} ({u.email})
                                  </option>
                                ))}
                            </select>
                            <div className="form-text mt-1 text-primary">
                              <i className="bi bi-info-circle me-1"></i>
                              Usa el buscador de arriba para filtrar esta lista si tienes muchos usuarios.
                            </div>
                          </div>

                          {/* SANDBOX AREA */}
                          <div className="p-3 border rounded bg-light">
                            <h6 className="mb-2 small text-uppercase fw-bold text-muted">Sandbox de Envío ({selectedUserIds.length})</h6>
                            {selectedUserIds.length === 0 ? (
                              <div className="text-center py-3 text-muted">
                                <i className="bi bi-basket2 fs-4 d-block mb-1"></i>
                                <small>Busca y añade usuarios para enviarlos aquí</small>
                              </div>
                            ) : (
                              <div className="d-flex flex-wrap gap-2">
                                {selectedUserIds.map(id => {
                                  const u = users.find(user => user.id === id);
                                  return (
                                    <div key={id} className="badge bg-white border text-dark d-flex align-items-center p-2 shadow-sm">
                                      {u?.full_name}
                                      <button
                                        type="button"
                                        className="btn-close ms-2"
                                        style={{ fontSize: '0.6rem' }}
                                        onClick={() => removeFromSandbox(id)}
                                      ></button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* --- PLAN TARGET --- */}
                      {notifTarget === 'PLAN' && (
                        <div className="mb-4">
                          <label className="form-label fw-bold">Elegir Plan Destinatario</label>
                          <div className="d-flex gap-2 flex-wrap">
                            {['free', 'medio', 'full', 'trial', 'vip'].map(p => (
                              <button
                                key={p}
                                type="button"
                                className={`btn btn-sm ${selectedPlan === p ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setSelectedPlan(p)}
                              >
                                {p.toUpperCase()}
                              </button>
                            ))}
                          </div>
                          <div className="form-text mt-2">
                            Se enviará a los <strong>{users.filter(u => !u.is_admin && u.plan.toLowerCase() === selectedPlan).length}</strong> usuarios que tienen este plan.
                          </div>
                        </div>
                      )}

                      <div className="mb-3">
                        <label className="form-label">Mensaje</label>
                        <textarea
                          className="form-control"
                          rows={4}
                          placeholder="Escribe el aviso importante aquí..."
                          value={notifMessage}
                          onChange={(e) => setNotifMessage(e.target.value)}
                          required
                        ></textarea>
                        <div className="form-text text-info">
                          <i className="bi bi-info-circle me-1"></i>
                          Este mensaje se enviará por Telegram a los usuarios vinculados y aparecerá en su panel web.
                        </div>
                      </div>

                      <div className="d-grid">
                        <button type="submit" className="btn btn-primary" disabled={sendingNotif}>
                          {sendingNotif ? (
                            <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                          ) : (
                            <><i className="bi bi-send me-2"></i>Enviar Ahora</>
                          )}
                        </button>
                      </div>

                      {commsStatus && (
                        <div className={`alert mt-4 animate__animated animate__fadeInUp ${commsStatus.type === 'success' ? 'alert-success border-success' : 'alert-danger shadow-sm'}`}>
                          <div className="d-flex align-items-center">
                            {commsStatus.type === 'success' ? (
                              <i className="bi bi-check-circle-fill fs-4 me-3"></i>
                            ) : (
                              <i className="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
                            )}
                            <strong>{commsStatus.msg}</strong>
                          </div>
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        {activeTab === 'devops' && (
          <div className="animate__animated animate__fadeIn">

            {/* ── Header de estado general ─────────────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="fw-bold mb-0"><i className="bi bi-display me-2 text-primary"></i>Centro de Monitoreo</h4>
                <small className="text-muted">
                  {lastRefresh ? `Última actualización: ${lastRefresh.toLocaleTimeString('es-AR')} · actualización automática cada 30s` : 'Cargando...'}
                </small>
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={fetchMonitorData}>
                <i className="bi bi-arrow-clockwise me-1"></i>Refrescar ahora
              </button>
            </div>

            {/* ── Fila 1: Tarjetas de estado de servicios ──────────────── */}
            <div className="row g-3 mb-4">
              {/* API Server */}
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #198754', borderRadius: 12 }}>
                  <div className="card-body d-flex align-items-center gap-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: '#d1fae5' }}>
                      <i className="bi bi-server fs-4 text-success"></i>
                    </div>
                    <div>
                      <div className="text-muted small">API Server</div>
                      <span className="badge bg-success">ONLINE</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Base de Datos */}
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #198754', borderRadius: 12 }}>
                  <div className="card-body d-flex align-items-center gap-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: '#d1fae5' }}>
                      <i className="bi bi-database fs-4 text-success"></i>
                    </div>
                    <div>
                      <div className="text-muted small">Base de Datos</div>
                      <span className="badge bg-success">ONLINE</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* WhatsApp */}
              <div className="col-6 col-md-3">
                <div
                  className="card border-0 shadow-sm h-100"
                  style={{
                    borderLeft: `4px solid ${
                      !waStatus ? '#6c757d' :
                      waStatus.ok ? '#198754' :
                      waStatus.state === 'connecting' ? '#ffc107' : '#dc3545'
                    }`,
                    borderRadius: 12
                  }}
                >
                  <div className="card-body d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 48, height: 48,
                        background: !waStatus ? '#f8f9fa' :
                          waStatus.ok ? '#d1fae5' :
                          waStatus.state === 'connecting' ? '#fff3cd' : '#fee2e2'
                      }}
                    >
                      <i className={`bi bi-whatsapp fs-4 ${
                        !waStatus ? 'text-secondary' :
                        waStatus.ok ? 'text-success' :
                        waStatus.state === 'connecting' ? 'text-warning' : 'text-danger'
                      }`}></i>
                    </div>
                    <div>
                      <div className="text-muted small">WhatsApp</div>
                      {!waStatus ? (
                        <span className="badge bg-secondary">...</span>
                      ) : waStatus.ok ? (
                        <span className="badge bg-success">CONECTADO</span>
                      ) : waStatus.state === 'connecting' ? (
                        <span className="badge bg-warning text-dark">RECONECTANDO</span>
                      ) : (
                        <span className="badge bg-danger">DESCONECTADO</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Scheduler */}
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #198754', borderRadius: 12 }}>
                  <div className="card-body d-flex align-items-center gap-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: '#d1fae5' }}>
                      <i className="bi bi-clock-history fs-4 text-success"></i>
                    </div>
                    <div>
                      <div className="text-muted small">Scheduler</div>
                      <span className="badge bg-success">ACTIVO</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Fila 2: Métricas del sistema ─────────────────────────── */}
            <div className="row g-3 mb-4">
              <div className="col-6 col-md">
                <div className="card border-0 shadow-sm text-center" style={{ borderRadius: 12 }}>
                  <div className="card-body py-3">
                    <div className="fs-2 fw-bold text-primary">{metrics ? metrics.total_users : '—'}</div>
                    <div className="text-muted small">Usuarios totales</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md">
                <div className="card border-0 shadow-sm text-center" style={{ borderRadius: 12 }}>
                  <div className="card-body py-3">
                    <div className="fs-2 fw-bold text-success">{metrics ? metrics.active_users_30d : '—'}</div>
                    <div className="text-muted small">Activos (30d)</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md">
                <div className="card border-0 shadow-sm text-center" style={{ borderRadius: 12 }}>
                  <div className="card-body py-3">
                    <div className="fs-2 fw-bold text-info">{metrics ? metrics.messages_today : '—'}</div>
                    <div className="text-muted small">Mensajes hoy</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md">
                <div className="card border-0 shadow-sm text-center" style={{ borderRadius: 12 }}>
                  <div className="card-body py-3">
                    <div className="fs-2 fw-bold text-warning">{metrics ? metrics.reminders_today : '—'}</div>
                    <div className="text-muted small">Recordatorios hoy</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md">
                <div className="card border-0 shadow-sm text-center" style={{ borderRadius: 12 }}>
                  <div className="card-body py-3">
                    <div className="fs-2 fw-bold" style={{ color: '#6f42c1' }}>{metrics ? metrics.google_linked : '—'}</div>
                    <div className="text-muted small">Google vinculado</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Fila 3: Logs + Acciones + Chat DevOps ───────────────── */}
            <div className="row g-3">

              {/* Logs de errores */}
              <div className="col-md-5">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                  <div className="card-header border-0 d-flex justify-content-between align-items-center" style={{ background: '#1e1e2e', borderRadius: '12px 12px 0 0' }}>
                    <span className="text-white fw-semibold"><i className="bi bi-journal-text me-2"></i>Log de Alertas del Sistema</span>
                    <button className="btn btn-sm btn-outline-light" onClick={fetchMonitorData}>
                      <i className="bi bi-arrow-clockwise"></i>
                    </button>
                  </div>
                  <div className="card-body p-0" style={{ background: '#0d0d1a' }}>
                    <div style={{ height: 320, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11, padding: '12px' }}>
                      {sysLogs.length === 0 ? (
                        <span className="text-success">✓ Sin errores ni advertencias recientes</span>
                      ) : (
                        [...sysLogs].reverse().map((line, i) => (
                          <div
                            key={i}
                            className="mb-1"
                            style={{
                              color: line.includes('ERROR') ? '#f87171' :
                                     line.includes('WARNING') ? '#fcd34d' : '#a3e635',
                              wordBreak: 'break-all'
                            }}
                          >
                            {line}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Acciones rápidas + Chat */}
              <div className="col-md-7">
                <div className="d-flex flex-column gap-3 h-100">

                  {/* Acciones rápidas */}
                  <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
                    <div className="card-header border-0 fw-semibold" style={{ background: '#f8f9fa' }}>
                      <i className="bi bi-lightning-charge-fill text-warning me-2"></i>Acciones Rápidas
                    </div>
                    <div className="card-body">
                      {reconnectMsg && (
                        <div className={`alert alert-${reconnectMsg.ok ? 'success' : 'danger'} py-2 mb-3`}>
                          <i className={`bi bi-${reconnectMsg.ok ? 'check-circle' : 'exclamation-triangle'}-fill me-2`}></i>
                          {reconnectMsg.text}
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={handleReconnectWhatsApp}
                          disabled={reconnecting || (waStatus?.ok === true)}
                        >
                          {reconnecting ? (
                            <><span className="spinner-border spinner-border-sm me-1"></span>Reconectando...</>
                          ) : (
                            <><i className="bi bi-whatsapp me-1"></i>Reconectar WhatsApp</>
                          )}
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => triggerDevOpsChat('Realiza un análisis completo del sistema y dame un reporte detallado.')}
                          disabled={devopsLoading}
                        >
                          <i className="bi bi-activity me-1"></i>Analizar Sistema (IA)
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => triggerDevOpsChat('Verificá la integridad de los usuarios en la base de datos.')}
                          disabled={devopsLoading}
                        >
                          <i className="bi bi-people me-1"></i>Verificar Usuarios
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Agente DevOps chat */}
                  <div className="card border-0 shadow-sm flex-grow-1" style={{ borderRadius: 12 }}>
                    <div className="card-header border-0 d-flex justify-content-between align-items-center" style={{ background: '#f8f9fa' }}>
                      <span className="fw-semibold"><i className="bi bi-robot me-2 text-primary"></i>Agente DevOps</span>
                      <button className="btn btn-sm btn-outline-danger" onClick={handleClearDevOpsChat} title="Limpiar historial">
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                    <div className="card-body" style={{ height: 220, overflowY: 'auto', background: '#fafafa' }}>
                      {devopsError && <div className="alert alert-danger py-2"><i className="bi bi-exclamation-triangle-fill me-2"></i>{devopsError}</div>}
                      {devopsSuccess && <div className="alert alert-success py-2 animate__animated animate__fadeIn"><i className="bi bi-check-circle-fill me-2"></i>{devopsSuccess}</div>}
                      {devopsChat.length === 0 ? (
                        <div className="text-center text-muted mt-4">
                          <i className="bi bi-terminal fs-2"></i>
                          <p className="small mt-2">Hacé una consulta técnica o pedí un diagnóstico.</p>
                        </div>
                      ) : (
                        devopsChat.map((msg, i) => (
                          <div key={i} className={`mb-2 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                            <div
                              className={`p-2 rounded-3 shadow-sm ${msg.role === 'user' ? 'bg-primary text-white' : 'bg-dark text-light'}`}
                              style={{ maxWidth: '88%', fontFamily: msg.role === 'assistant' ? 'monospace' : 'inherit', fontSize: 13 }}
                            >
                              <div className="small opacity-50 mb-1">{msg.role === 'user' ? 'Admin' : 'DevOps Agent'}</div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                            </div>
                          </div>
                        ))
                      )}
                      {devopsLoading && (
                        <div className="text-center mt-2">
                          <div className="spinner-border text-primary spinner-border-sm"></div>
                          <small className="ms-2 text-muted">Procesando...</small>
                        </div>
                      )}
                    </div>
                    <div className="card-footer border-0" style={{ background: '#f8f9fa' }}>
                      <form onSubmit={handleDevOpsChat} className="input-group">
                        <span className="input-group-text bg-white font-monospace">$</span>
                        <input
                          type="text"
                          className="form-control font-monospace"
                          placeholder="Consulta técnica..."
                          value={devopsInput}
                          onChange={(e) => setDevopsInput(e.target.value)}
                          disabled={devopsLoading}
                        />
                        <button className="btn btn-dark" type="submit" disabled={!devopsInput.trim() || devopsLoading}>
                          <i className="bi bi-send-fill"></i>
                        </button>
                      </form>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="animate__animated animate__fadeIn">
            <div className="row">
              <div className="col-md-5">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Tickets de Soporte</h5>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="escalatedSwitch"
                        checked={showEscalatedOnly}
                        onChange={(e) => setShowEscalatedOnly(e.target.checked)}
                      />
                      <label className="form-check-label small fw-bold text-danger" htmlFor="escalatedSwitch">
                        Requieren Atención
                      </label>
                    </div>
                  </div>
                  <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: '600px' }}>
                    {tickets.length === 0 ? (
                      <div className="p-4 text-center text-muted">No hay tickets registrados.</div>
                    ) : (
                      tickets
                        .filter(t => !showEscalatedOnly || t.requires_human) // Filter logic
                        .map(ticket => (
                          <button
                            key={ticket.id}
                            className={`list-group-item list-group-item-action d-flex justify-content-between align-items-start ${selectedTicket?.id === ticket.id ? 'active' : ''} ${!ticket.is_read ? 'fw-bold bg-light' : ''}`}
                            onClick={() => handleSelectTicket(ticket)}
                          >
                            <div className="ms-2 me-auto">
                              <div className="fw-bold d-flex align-items-center gap-2">
                                Ticket #{ticket.id}
                                {ticket.status === 'open' ? (
                                  <span className="badge bg-success rounded-pill" style={{ fontSize: '0.7em' }}>ABIERTO</span>
                                ) : (
                                  <span className="badge bg-secondary rounded-pill" style={{ fontSize: '0.7em' }}>CERRADO</span>
                                )}
                                {ticket.requires_human && (
                                  <span className="badge bg-danger animate__animated animate__pulse animate__infinite">REQUIERE ATENCIÓN</span>
                                )}
                              </div>
                              <small className="text-muted d-block text-truncate" style={{ maxWidth: '250px' }}>
                                {ticket.issue}
                              </small>
                              <small className="text-primary fw-bold" style={{ fontSize: '0.9em' }}>
                                <i className="bi bi-person-fill me-1"></i>
                                {ticket.user?.full_name || `Usuario ID: ${ticket.user_id}`}
                              </small>
                              <div className="text-muted" style={{ fontSize: '0.75em' }}>
                                {ticket.user?.email}
                              </div>
                            </div>
                            {!ticket.is_read && <span className="badge bg-primary rounded-pill">NUEVO</span>}
                          </button>
                        ))
                    )}
                  </div>
                </div>
              </div>

              {/* Ticket Detail & Chat */}
              {selectedTicket && (
                <div className="col-md-7">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                      <div>
                        <h6 className="mb-0 fw-bold">Conversación con {selectedTicket.user?.full_name}</h6>
                        <small className="text-muted">{selectedTicket.user?.email}</small>
                      </div>
                      <button className="btn btn-sm btn-danger" onClick={() => handleCloseTicket(selectedTicket.id)}>
                        <i className="bi bi-check-circle me-1"></i>Cerrar Ticket
                      </button>
                    </div>

                    <div className="card-body bg-light" style={{ height: '500px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                      <div className="alert alert-info py-2 small mb-3">
                        <strong>Problema inicial:</strong> {selectedTicket.issue}
                      </div>

                      {ticketLoading ? (
                        <div className="text-center my-auto"><div className="spinner-border text-primary"></div></div>
                      ) : (
                        ticketHistory.map((msg, i) => (
                          <div key={i} className={`mb-3 d-flex ${msg.role === 'user' ? 'justify-content-start' : 'justify-content-end'}`}>
                            <div className={`p-3 rounded-3 shadow-sm ${msg.role === 'user' ? 'bg-white text-dark border' : 'bg-primary text-white'}`} style={{ maxWidth: '80%' }}>
                              <div className="small opacity-75 mb-1 fw-bold">
                                {msg.role === 'user' ? (selectedTicket.user?.full_name || 'Usuario') :
                                  msg.role === 'admin' ? 'Tú (Admin)' : 'Asistente IA'}
                              </div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="card-footer bg-white border-0 py-3">
                      <form onSubmit={handleReplyTicket} className="input-group shadow-sm">
                        <input
                          type="text"
                          className="form-control border-end-0"
                          placeholder="Responder al usuario como Administrador..."
                          value={ticketReply}
                          onChange={(e) => setTicketReply(e.target.value)}
                          disabled={selectedTicket.status === 'closed'}
                        />
                        <button className="btn btn-primary px-4" type="submit" disabled={!ticketReply.trim() || selectedTicket.status === 'closed'}>
                          <i className="bi bi-send-fill"></i>
                        </button>
                      </form>
                      <div className="form-text mt-2 text-center">
                        <i className="bi bi-info-circle me-1"></i>Tu respuesta aparecerá en el chat del usuario con tu nombre: <strong>[{currentUser?.full_name.split(' ')[0] || 'ADMIN'}]</strong>.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
